import React, { useEffect } from 'react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
  Box, Stack, TextField, IconButton, Button, Autocomplete
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import { Controller, FormProvider, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import type { EstimateDto } from '../../contracts/estimates/types';

const UNIT_CHOICES = [' ', '개', '대', '박스', '시간', '일'];

const toNumber = (v: unknown) => {
  if (typeof v === 'number' && isFinite(v)) return v;
  if (v == null) return 0;
  const n = Number(String(v).replace(/,/g, '').trim());
  return isNaN(n) ? 0 : n;
};

/** 드래그 가능한 행 래퍼 */
function SortableRow({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: 'transparent',
  };
  return (
    <div ref={setNodeRef} style={style}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <IconButton {...attributes} {...listeners} size="small" sx={{ cursor: 'grab', mt: 0.5 }}>
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }}>{children}</Box>
      </Stack>
    </div>
  );
}

/** 금액 필드: qty*unitPrice 변화 시 amount에 저장 + 표시 */
function AmountField({ index }: { index: number }) {
  const { control, setValue } = useFormContext<EstimateDto>();
  const qty = useWatch({ control, name: `content.items.${index}.qty` });
  const unitPrice = useWatch({ control, name: `content.items.${index}.unitPrice` });

  React.useEffect(() => {
    const amount = toNumber(qty) * toNumber(unitPrice);
    setValue(`content.items.${index}.amount`, amount, { shouldDirty: true, shouldValidate: true });
  }, [qty, unitPrice, index, setValue, control]);

  return (
    <Controller
      name={`content.items.${index}.amount`}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          label="금액"
          sx={{ width: 200 }}
          type="text"
          inputMode="numeric"
          value={toNumber(field.value).toLocaleString('ko-KR')}
          InputProps={{ readOnly: true }}
        />
      )}
    />
  );
}

type ItemsEditorProps = {
  name?: 'content.items' | string;
  initialItems?: EstimateDto['content']['items'];
};

/** 아이템/설명 편집 섹션 (커뮤니티 무료 버전) */
export function ItemsEditor({ name = 'content.items', initialItems }: ItemsEditorProps) {
  const { control } = useFormContext<EstimateDto>(); // 반드시 FormProvider 하위에서 사용
  const { fields, append, remove, move, replace } = useFieldArray({
    control,
    name: 'content.items',
  });

  useEffect(() => {
    if (initialItems && initialItems.length > 0) {
      replace(initialItems); // 🔁 기존 리스트로 교체
    }
  }, [initialItems, replace]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex((f) => f.id === active.id);
    const newIndex = fields.findIndex((f) => f.id === over.id);
    move(oldIndex, newIndex);
  };

  const addItem = () =>
    append({
      item: '',
      size: '',
      unit: '',
      qty: 0,
      unitPrice: 0,
      amount: 0,
      remark: '',
      description: null, // ← null이면 “품목 행”
    } as any);

  const addDesc = () =>
    append({
      item: '',
      size: '',
      unit: '',
      qty: 0,
      unitPrice: 0,
      amount: 0,
      remark: '',
      description: '', // ← string이면 “설명 행”
    } as any);

  return (
    <Box sx={{ p: 2 }}>
      {/* 드래그 가능한 리스트 */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
          {fields.map((item, index) => (
            <SortableRow key={item.id} id={item.id}>
                
              {(item.description === null || !Object.prototype.hasOwnProperty.call(item, 'description')) ? (
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="flex-start"
                  sx={{
                    py: 1,
                    flexWrap: 'wrap',
                    rowGap: 1,
                  }}
                >
                  {/* ① 품명 */}
                  <Controller
                    name={`content.items.${index}.item`}
                    control={control}
                    rules={{ required: '품명을 입력하세요' }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="품명"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message || '품명을 입력하세요'}
                        sx={{ minWidth: 200 }}
                      />
                    )}
                  />
                  {/* ② 규격 */}
                  <Controller
                    name={`content.items.${index}.size`}
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="규격" sx={{ minWidth: 200 }} />
                    )}
                  />
                  {/* ③ 단위 Autocomplete */}
                  <Controller
                    name={`content.items.${index}.unit`}
                    control={control}
                    rules={{ required: '단위를 입력하세요' }}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        freeSolo
                        options={UNIT_CHOICES}
                        value={field.value || ''}
                        onChange={(_, v) => field.onChange(v ?? '')}
                        onInputChange={(_, v) => field.onChange(v)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="단위"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || '단위를 입력하세요'}
                            sx={{ width: 160 }}
                          />
                        )}
                      />
                    )}
                  />
                  {/* ④ 수량 */}
                  <Controller
                    name={`content.items.${index}.qty`}
                    control={control}
                    
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        type="text"
                        inputMode="numeric"
                        label="수량"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        sx={{ width: 90 }}
                        onKeyDown={(e) => {
                          if (/^\d$/.test(e.key) && toNumber(field.value) === 0) {
                              e.preventDefault();
                              field.onChange(Number(e.key));
                          }
                        }}
                        onChange={(e) => {
                          if (e.target.value === "-" || e.target.value === "") {
                              field.onChange(e.target.value);
                          } else {
                              field.onChange(toNumber(e.target.value));
                          }
                        }}
                      />
                    )}
                  />
                  {/* ⑤ 단가(콤마 포맷) */}
                  <Controller
                    name={`content.items.${index}.unitPrice`}
                    control={control}
                    render={({ field }) => {
                      const display =
                        !field.value || toNumber(field.value) === 0
                          ? ''
                          : toNumber(field.value).toLocaleString('ko-KR');
                      return (
                        <TextField
                          label="단가"
                          placeholder="0"
                          sx={{ width: 200 }}
                          type="text"
                          inputMode="numeric"
                          value={display}
                          onKeyDown={(e) => {
                            if (/^\d$/.test(e.key) && toNumber(field.value) === 0) {
                              e.preventDefault();
                              field.onChange(Number(e.key));
                            }
                          }}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/,/g, '');
                            field.onChange(raw === '' ? 0 : toNumber(raw));
                          }}
                          onFocus={(e) => e.target.select()}
                        />
                      );
                    }}
                  />
                  {/* ⑥ 금액(자동 계산 + 저장) */}
                  <AmountField index={index} />
                  {/* ⑦ 비고 */}
                  <Controller
                    name={`content.items.${index}.remark`}
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="비고" sx={{ width: 240 }} />
                    )}
                  />
                  {/* 삭제 */}
                  <IconButton onClick={() => remove(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              ) : (
                // 설명 행
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="flex-start"
                  sx={{
                    py: 1,
                    flexWrap: 'wrap',
                    rowGap: 1,
                  }}
                >
                  <Controller
                    name={`content.items.${index}.description`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="설명"
                        sx={{ flex: 1, minWidth: 280 }}
                      />
                    )}
                  />
                  <IconButton onClick={() => remove(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              )}
            </SortableRow>
          ))}
        </SortableContext>
      </DndContext>

      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button onClick={addItem}>+ 아이템 행 추가</Button>
        <Button onClick={addDesc}>+ 설명 추가</Button>
      </Stack>
    </Box>
  );
}
